// Fachsprache und Aufruf des Kontaktstatus (ST-PTY-001, CM-PTY-008).
// REINES MODELL, kein React, kein DOM; die Antwort liest der Befehls-Client.
//
// "Kontakt bestaetigen" macht aus einem Entwurf einen aktiven Kontakt. Fehlt
// dafuer etwas, nennt die Oberflaeche genau EINEN Mangel und den Weg dorthin
// (NFR-UX-005); die Reihenfolge legt der Server fest.

import type { PlainMessage } from './access.ts';
import { sendCommand, type CommandMessages, type CommandOutcome, type SendLike } from './command-client.ts';
import { fetchDuplicateCandidates } from './duplicate-candidates.ts';
import { addressesHref } from './addresses.ts';
import { contactPointsHref, organizationHref, partyHref, type PartyDetail } from './parties.ts';

export interface PartyConfirmed {
  readonly lifecycleStatus: string;
  readonly lockVersion: number;
  /** `false`: der Kontakt war schon aktiv; nichts wurde geschrieben. */
  readonly changed: boolean;
  readonly phoneMatch?: { readonly partyId: string; readonly displayName: string };
  /** Dieselbe E-Mail an einer inaktiven oder zusammengefuehrten Party: Hinweis, keine Sperre. */
  readonly emailMatch?: { readonly partyId: string; readonly displayName: string };
}

export const NAME_MISSING_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Der Kontakt ist noch nicht bestätigt.',
  explanation: 'Es fehlt der Nachname. Ohne ihn lässt sich der Kontakt schlecht wiederfinden.',
  nextStep: 'Trage im Abschnitt „Name“ einen Nachnamen ein und bestätige dann erneut.',
  retryable: false,
});

export const ORGANIZATION_NAME_MISSING_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Der Kontakt ist noch nicht bestätigt.',
  explanation: 'Es fehlt der Name der Organisation.',
  nextStep: 'Trage den Namen der Organisation ein und bestätige dann erneut.',
  retryable: false,
});

export const UNREACHABLE_PARTY_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Der Kontakt ist noch nicht bestätigt.',
  explanation: 'Es fehlt ein Kontaktweg oder eine Anschrift. Ohne sie ist der Kontakt nicht erreichbar.',
  nextStep: 'Füge eine E-Mail-Adresse, eine Telefonnummer oder eine Anschrift hinzu und bestätige dann erneut.',
  retryable: false,
});

export const EMAIL_DUPLICATE_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Der Kontakt ist noch nicht bestätigt.',
  explanation: 'Dieselbe E-Mail-Adresse steht schon bei einem anderen Kontakt. Vermutlich ist es derselbe.',
  nextStep: 'Prüfe, ob es derselbe Kontakt ist. Wenn nicht, entferne die Adresse bei dem Kontakt, zu dem sie nicht gehört.',
  retryable: false,
});

export const PARTY_CONFIRM_MESSAGES: CommandMessages = Object.freeze({
  notFound: Object.freeze({
    headline: 'Diesen Kontakt gibt es hier nicht.',
    // Keine Vermutung ueber eine Berechtigung: der Server beantwortet beides gleich.
    explanation: 'Er wurde entfernt, oder die Adresse ist nicht mehr gültig.',
    nextStep: 'Geh zurück zur Kontaktliste und öffne den Kontakt dort erneut.',
    retryable: false,
  }),
  conflictFallback: Object.freeze({
    headline: 'Jemand anderes war schneller.',
    explanation: 'Der Kontakt hat sich geändert, seit du die Seite geöffnet hast. Er wurde nicht bestätigt.',
    nextStep: 'Die Seite zeigt jetzt den aktuellen Stand. Prüfe ihn.',
    retryable: false,
  }),
  conflicts: Object.freeze({
    party_not_confirmable: Object.freeze({
      headline: 'Dieser Kontakt lässt sich nicht bestätigen.',
      explanation: 'Bestätigen lässt sich nur ein Entwurf. Der Kontakt hat inzwischen einen anderen Status.',
      nextStep: 'Die Seite zeigt jetzt seinen Status.',
      retryable: false,
    }),
  }),
  unprocessable: Object.freeze({
    party_name_missing: NAME_MISSING_MESSAGE,
    party_unreachable: UNREACHABLE_PARTY_MESSAGE,
    party_email_duplicate: EMAIL_DUPLICATE_MESSAGE,
  }),
}) satisfies CommandMessages;

export const CONFIRM_LABEL = 'Kontakt bestätigen';
/** Die volle Regel am Knopf, je Art: eine Organisation hat keinen Nachnamen. */
export function confirmHint(istOrganisation: boolean): string {
  const name = istOrganisation ? 'der Name der Organisation' : 'ein Nachname';
  return `Nötig sind ${name} und ein Kontaktweg oder eine Anschrift; die E-Mail-Adresse darf nicht schon bei einem anderen Kontakt stehen. Danach gilt der Kontakt als aktiv, zurück zum Entwurf geht es nicht.`;
}
export const ALREADY_CONFIRMED = 'Der Kontakt war bereits bestätigt.';

/** Die Wirkung, nicht die Handlung (CM-UX-004). */
export function confirmation(applied: PartyConfirmed): string {
  return applied.changed ? 'Der Kontakt ist jetzt aktiv.' : ALREADY_CONFIRMED;
}

/**
 * Der Stand der Party. Bei einer Person fuehrt ihn auch der Namensblock, und
 * der Namensabschnitt schreibt ihn nach dem Speichern nur dort nach; er ist
 * dann der juengere.
 */
export function partyLockVersion(party: Pick<PartyDetail, 'lockVersion' | 'person'>): number {
  return party.person?.lockVersion ?? party.lockVersion ?? 0;
}

/** Der Weg zum Mangel: ein Abschnitt dieser Seite oder eine Pflegeseite. */
export type NextStepLink =
  | { readonly label: string; readonly section: 'name' }
  | { readonly label: string; readonly href: string };

export function messageFor(message: PlainMessage, party: Pick<PartyDetail, 'organization'>): PlainMessage {
  return message === NAME_MISSING_MESSAGE && party.organization !== undefined ? ORGANIZATION_NAME_MISSING_MESSAGE : message;
}

/**
 * Die Wege zum Mangel. Bei der Dublette nennt die Seite den anderen Kontakt;
 * findet sie ihn nicht, bleibt der Weg zu den eigenen Kontaktwegen.
 */
export function nextStepsFor(
  message: PlainMessage | undefined,
  party: Pick<PartyDetail, 'partyId' | 'organization'>,
): readonly NextStepLink[] {
  if (message === NAME_MISSING_MESSAGE) {
    return party.organization === undefined
      ? [{ label: 'Nachnamen eintragen', section: 'name' }]
      : [{ label: 'Organisationsnamen pflegen', href: organizationHref(party.partyId) }];
  }
  if (message === UNREACHABLE_PARTY_MESSAGE) {
    return [
      { label: 'Kontaktwege pflegen', href: contactPointsHref(party.partyId) },
      { label: 'Anschriften pflegen', href: addressesHref(party.partyId) },
    ];
  }
  if (message === EMAIL_DUPLICATE_MESSAGE) {
    return [{ label: 'Kontaktwege dieses Kontakts pflegen', href: contactPointsHref(party.partyId) }];
  }
  return [];
}

export async function confirmParty(options: {
  readonly baseUrl: string;
  readonly accessToken: string;
  readonly partyId: string;
  readonly lockVersion: number;
  readonly send?: SendLike;
}): Promise<CommandOutcome<PartyConfirmed>> {
  return sendCommand({
    url: `${options.baseUrl}/parties/${encodeURIComponent(options.partyId)}/confirm`,
    accessToken: options.accessToken,
    body: { lockVersion: options.lockVersion },
    messages: PARTY_CONFIRM_MESSAGES,
    applied: statusApplied,
    ...(options.send === undefined ? {} : { send: options.send }),
  });
}

function statusApplied(record: Record<string, unknown>): PartyConfirmed {
  const phoneMatch = hintOf(record.phoneMatch);
  const emailMatch = hintOf(record.emailMatch);
  return {
    lifecycleStatus: typeof record.lifecycleStatus === 'string' ? record.lifecycleStatus : '',
    lockVersion: typeof record.lockVersion === 'number' ? record.lockVersion : 0,
    changed: record.changed === true,
    ...(phoneMatch === undefined ? {} : { phoneMatch }),
    ...(emailMatch === undefined ? {} : { emailMatch }),
  };
}

function hintOf(value: unknown): { readonly partyId: string; readonly displayName: string } | undefined {
  const match = (value ?? undefined) as Record<string, unknown> | undefined;
  return match !== undefined && typeof match.partyId === 'string' && typeof match.displayName === 'string'
    ? { partyId: match.partyId, displayName: match.displayName }
    : undefined;
}

/** Wie der Server: nur eine Dublette in diesen Zustaenden blockiert. */
const BLOCKING_STATUSES: readonly string[] = Object.freeze(['draft', 'active']);

/**
 * Welcher andere Kontakt traegt dieselbe E-Mail-Adresse? Dieselbe Pruefung wie
 * vor der Anlage (gleiche Normalisierung, nur Sichtbares), gefiltert auf die
 * gleiche Art; der Server nennt im 422 keinen Namen.
 */
export async function findEmailDuplicate(options: {
  readonly baseUrl: string;
  readonly accessToken: string;
  readonly party: Pick<PartyDetail, 'partyId' | 'kind' | 'emailAddresses'>;
  readonly send?: SendLike;
}): Promise<{ readonly displayName: string; readonly href: string } | undefined> {
  for (const address of options.party.emailAddresses) {
    const result = await fetchDuplicateCandidates({
      baseUrl: options.baseUrl,
      accessToken: options.accessToken,
      email: address.value,
      ...(options.send === undefined ? {} : { send: options.send }),
    });
    if (result.outcome !== 'geprueft') {
      return undefined;
    }
    const other = result.candidates.find(
      (candidate) =>
        candidate.partyId !== options.party.partyId &&
        candidate.kind === options.party.kind &&
        BLOCKING_STATUSES.includes(candidate.lifecycleStatus) &&
        candidate.matchesEmailAddress,
    );
    if (other !== undefined) {
      return { displayName: other.displayName, href: partyHref(other.partyId) };
    }
  }
  return undefined;
}

// --- Deaktivieren und Wieder aufnehmen -----------------------------------------

/** Nicht vorbelegt: ein Standardgrund wuerde unreflektiert bestaetigt (NFR-UX-002). */
export const PARTY_END_REASONS: readonly { readonly code: string; readonly label: string; readonly nurPerson?: true }[] = Object.freeze([
  { code: 'no_longer_relevant', label: 'Kein Bedarf mehr' },
  { code: 'left_organization', label: 'Nicht mehr in dieser Funktion oder Organisation', nurPerson: true },
  { code: 'unreachable', label: 'Nicht mehr erreichbar' },
  { code: 'duplicate', label: 'Doppelt angelegt' },
  { code: 'other', label: 'Anderer Grund' },
]);

/** Die Gruende fuer diese Art Kontakt: eine Organisation verlaesst keine Funktion. */
export function endReasonsFor(party: Pick<PartyDetail, 'person'>): readonly { readonly code: string; readonly label: string }[] {
  return PARTY_END_REASONS.filter((grund) => grund.nurPerson !== true || party.person !== undefined);
}

export const DEACTIVATE_LABEL = 'Kontakt deaktivieren';
export const DEACTIVATE_QUESTION = 'Warum wird der Kontakt deaktiviert?';
export const DEACTIVATE_EFFECT =
  'Der Kontakt bleibt auffindbar und pflegbar und ist als inaktiv gekennzeichnet. Du kannst ihn später wieder aufnehmen und erneut bestätigen.';
export const REACTIVATE_LABEL = 'Wieder aufnehmen';
export const REACTIVATE_HINT = 'Der Kontakt wird wieder ein Entwurf; danach kannst du ihn bestätigen.';
export const ALREADY_INACTIVE = 'Der Kontakt war bereits inaktiv.';
export const ALREADY_DRAFT = 'Der Kontakt war bereits ein Entwurf.';

export const PARTY_STATUS_MESSAGES: CommandMessages = Object.freeze({
  notFound: PARTY_CONFIRM_MESSAGES.notFound,
  conflictFallback: PARTY_CONFIRM_MESSAGES.conflictFallback,
  conflicts: Object.freeze({
    party_not_deactivatable: Object.freeze({
      headline: 'Dieser Kontakt lässt sich nicht deaktivieren.',
      explanation: 'Er hat inzwischen einen Status, der sich so nicht ändern lässt.',
      nextStep: 'Die Seite zeigt jetzt seinen Status.',
      retryable: false,
    }),
    party_not_reactivatable: Object.freeze({
      headline: 'Dieser Kontakt lässt sich nicht wieder aufnehmen.',
      explanation: 'Wieder aufnehmen lässt sich nur ein deaktivierter Kontakt.',
      nextStep: 'Die Seite zeigt jetzt seinen Status.',
      retryable: false,
    }),
  }),
}) satisfies CommandMessages;

export function deactivationConfirmation(applied: PartyConfirmed): string {
  return applied.changed ? 'Der Kontakt ist jetzt inaktiv.' : ALREADY_INACTIVE;
}

export function reactivationConfirmation(applied: PartyConfirmed): string {
  return applied.changed ? 'Der Kontakt ist wieder ein Entwurf. Bestätige ihn, sobald die Angaben stimmen.' : ALREADY_DRAFT;
}

export function deactivateParty(options: {
  readonly baseUrl: string;
  readonly accessToken: string;
  readonly partyId: string;
  readonly lockVersion: number;
  readonly endReasonCode: string;
  readonly send?: SendLike;
}): Promise<CommandOutcome<PartyConfirmed>> {
  return sendCommand({
    url: `${options.baseUrl}/parties/${encodeURIComponent(options.partyId)}/deactivate`,
    accessToken: options.accessToken,
    body: { lockVersion: options.lockVersion, endReasonCode: options.endReasonCode },
    messages: PARTY_STATUS_MESSAGES,
    applied: statusApplied,
    ...(options.send === undefined ? {} : { send: options.send }),
  });
}

export function reactivateParty(options: {
  readonly baseUrl: string;
  readonly accessToken: string;
  readonly partyId: string;
  readonly lockVersion: number;
  readonly send?: SendLike;
}): Promise<CommandOutcome<PartyConfirmed>> {
  return sendCommand({
    url: `${options.baseUrl}/parties/${encodeURIComponent(options.partyId)}/reactivate`,
    accessToken: options.accessToken,
    body: { lockVersion: options.lockVersion },
    messages: PARTY_STATUS_MESSAGES,
    applied: statusApplied,
    ...(options.send === undefined ? {} : { send: options.send }),
  });
}

// --- Beschreibbare Zustaende ---------------------------------------------------

/**
 * Die Zustaende, in denen an einer Party etwas Neues entstehen oder sich ihre
 * Sichtbarkeit aendern darf. SPIEGEL von WRITABLE_LIFECYCLE_STATUSES in
 * apps/api/src/domain/write-core/lifecycle.ts: das Web blendet damit nur
 * Befehle aus, die der Server ohnehin mit 409 party_not_writable ablehnte.
 * Entscheiden tut der Server; test/party-share.test.ts haelt beide Listen
 * gleich, damit der Spiegel nicht still auseinanderlaeuft.
 */
export const WRITABLE_LIFECYCLE_STATUSES: readonly string[] = Object.freeze(['draft', 'active', 'inactive', 'deceased', 'dissolved']);

/** Ein fehlender oder unbekannter Zustand ist nicht beschreibbar, wie am Server. */
export function isWritableLifecycle(status: unknown): boolean {
  return WRITABLE_LIFECYCLE_STATUSES.includes(String(status));
}

// --- Satz an nicht beschreibbaren Kontakten ------------------------------------

export interface NotWritableNotice {
  readonly headline: string;
  readonly explanation: string;
}

// Kein Link zum weitergefuehrten Kontakt: `merged_into_party_id` darf im Anwendungscode nicht
// vorkommen (Strukturprobe TX-053, ADR-0065 Ziffer 4).
const NOT_WRITABLE_NOTICES: Readonly<Record<string, NotWritableNotice>> = Object.freeze({
  archived: Object.freeze({
    headline: 'Dieser Kontakt ist archiviert.',
    explanation: 'Du kannst alles ansehen, aber nichts Neues mehr eintragen.',
  }),
  anonymized: Object.freeze({
    headline: 'Dieser Kontakt ist anonymisiert.',
    explanation: 'Seine persönlichen Angaben wurden entfernt; Neues lässt sich nicht mehr eintragen.',
  }),
  merged: Object.freeze({
    headline: 'Dieser Kontakt wurde mit einem anderen zusammengeführt.',
    explanation: 'Neues trägst du beim weitergeführten Kontakt ein; such ihn in der Kontaktliste.',
  }),
});

/**
 * Der eine Satz, der an einem nicht beschreibbaren Kontakt erklaert, warum nichts Neues angeboten
 * wird. Nur die drei bekannten Zustaende haben einen: bei einem unbekannten oder noch nicht
 * geladenen Zustand bleibt das Angebot stehen, der Server lehnt dann mit seinem Satz ab.
 */
export function notWritableNotice(status: unknown): NotWritableNotice | undefined {
  return isWritableLifecycle(status) ? undefined : NOT_WRITABLE_NOTICES[String(status)];
}
